import React, { Component } from 'react';
import { StaticQuery, graphql } from 'gatsby';
import smoothscroll from 'smoothscroll-polyfill';
import StoreContext, { defaultStoreContext } from '../../context/StoreContext';
import {
  Header,
  Footer,
  NavigationMenu,
} from '.';
import {
  MiniCart,
} from '../shop';
import { SEO } from '..';
import '../../sass/global/styles.scss';

const isClient = typeof window !== 'undefined';
const viewportUnitsBuggyfill = isClient ? require('viewport-units-buggyfill') : null;

const countQuantity = (lineItems) => {
  let quantity = 0;
  lineItems.forEach((lineItem) => {
    quantity += lineItem.quantity;
  });
  return quantity;
};

class PureLayout extends Component {
  state = {
    navOpen: false,
    minicartOpen: false,
    navIsAnimating: false,
    store: {
      ...defaultStoreContext,
      setFetching: (isFetching) => {
        this.setState(state => ({
          store: {
            ...state.store,
            fetching: isFetching,
          },
        }));
      },
      addVariantToCart: (variantId, quantity) => {
        if (variantId === '' || !quantity) {
          console.error('Both a size and quantity are required.');
          return;
        }

        this.setState(state => ({
          store: {
            ...state.store,
            adding: true,
          },
        }));
        const { store } = this.state;
        const { checkout, client } = store;
        const checkoutId = checkout.id;
        const lineItemsToUpdate = [
          { variantId, quantity: parseInt(quantity, 10) },
        ];

        return client.checkout
          .addLineItems(checkoutId, lineItemsToUpdate)
          .then((newCheckout) => {
            this.setState(state => ({
              store: {
                ...state.store,
                checkout: newCheckout,
                adding: false,
              },
            }));
          });
      },
      removeLineItem: (client, checkoutID, lineItemID) => (
        client.checkout
          .removeLineItems(checkoutID, [lineItemID])
          .then((res) => {
            this.setState(state => ({
              store: {
                ...state.store,
                checkout: res,
              },
            }));
          })
      ),
      updateLineItem: (client, checkoutID, lineItemID, quantity) => {
        const lineItemsToUpdate = [
          { id: lineItemID, quantity: parseInt(quantity, 10) },
        ];

        return client.checkout
          .updateLineItems(checkoutID, lineItemsToUpdate)
          .then((res) => {
            this.setState(state => ({
              store: {
                ...state.store,
                checkout: res,
              },
            }));
          });
      },
    },
  };

  componentDidMount() {
    if (isClient) {
      viewportUnitsBuggyfill.init();
      smoothscroll.polyfill();
      this.initializeCheckout();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      store: {
        adding: isAdding,
        checkout: {
          lineItems: currCartItems,
        },
      },
    } = this.state;
    const {
      store: {
        adding: wasAdding,
        checkout: {
          lineItems: prevCartItems,
        },
      },
    } = prevState;
    const prevQuantity = countQuantity(prevCartItems);
    const currQuantity = countQuantity(currCartItems);
    if (wasAdding !== isAdding) {
      if (currQuantity > prevQuantity) {
        this.setState({ // eslint-disable-line
          minicartOpen: true,
        });
      }
    }
  }

  toggleNav = (event) => {
    event.preventDefault();
    const { navOpen } = this.state;
    if (event) event.preventDefault();
    this.setState({
      navIsAnimating: true,
    });
    if (navOpen) document.body.classList.remove('nav-open');
    if (!navOpen) document.body.classList.add('nav-open');
    setTimeout(() => {
      this.setState({
        navIsAnimating: false,
        navOpen: !navOpen,
      });
    }, 500);
  };

  openNav = (event) => {
    if (event) event.preventDefault();
    document.body.classList.add('nav-open');
    this.setState({
      navOpen: true,
    });
  };

  closeNav = () => {
    document.body.classList.remove('nav-open');
    this.setState({
      navOpen: false,
    });
  };

  toggleMinicart = (event) => {
    event.preventDefault();
    const { minicartOpen } = this.state;
    if (event) event.preventDefault();
    this.setState({
      minicartOpen: !minicartOpen,
    });
  };

  openMinicart = (event) => {
    if (event) event.preventDefault();
    this.setState({
      minicartOpen: true,
    });
  };

  closeMinicart = () => {
    this.setState({
      minicartOpen: false,
    });
  };

  async initializeCheckout() {
    // Check for an existing cart.
    const {
      store: {
        client,
        setFetching,
      },
    } = this.state;
    setFetching(true);

    const existingCheckoutID = isClient ? localStorage.getItem('shopify_checkout_id') : null;

    const setCheckoutInState = (checkout) => {
      if (isClient) {
        localStorage.setItem('shopify_checkout_id', checkout.id);
      }

      this.setState(state => ({
        store: {
          ...state.store,
          checkout,
        },
      }));
    };

    const createNewCheckout = () => client.checkout.create();
    const fetchCheckout = id => client.checkout.fetch(id);

    if (existingCheckoutID) {
      try {
        const checkout = await fetchCheckout(existingCheckoutID);
        // Make sure this cart hasn’t already been purchased.
        if (!checkout.completedAt) {
          setCheckoutInState(checkout);
          setFetching(false);
          return;
        }
      } catch (e) {
        localStorage.setItem('shopify_checkout_id', null);
      }
    }

    const newCheckout = await createNewCheckout();
    setCheckoutInState(newCheckout);
    setFetching(false);
  }

  render() {
    const {
      navOpen,
      minicartOpen,
      navIsAnimating,
      store,
    } = this.state;
    const {
      children,
      location,
      settings,
      seoData,
      cursorX,
      cursorY,
      viewportWidth,
      viewportHeight,
      documentHeight,
      viewportTopPos,
    } = this.props;
    const { primaryMenu } = settings;
    const {
      metaTitle = null,
      metaDescription = null,
      openGraphImage = null,
    } = seoData;
    const isHome = Boolean(location.pathname === '/');
    return (
      <StoreContext.Provider value={store}>
        <SEO
          title={metaTitle && metaTitle.text}
          desc={metaDescription && metaDescription.text}
          banner={openGraphImage && openGraphImage.url}
          pathname={location.pathname}
        />
        <div id="app" className="app">
          <Header
            navOpen={navOpen}
            minicartOpen={minicartOpen}
            navIsAnimating={navIsAnimating}
            location={location}
            navigation={primaryMenu}
            toggleNavHandler={event => this.toggleNav(event)}
            toggleMinicartHandler={event => this.toggleMinicart(event)}
            cursorX={cursorX}
            cursorY={cursorY}
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            documentHeight={documentHeight}
            viewportTopPos={viewportTopPos}
            isShop
          />
          <NavigationMenu
            navOpen={navOpen}
            isHome={isHome}
            navIsAnimating={navIsAnimating}
            location={location}
            settings={settings}
            closeNav={event => this.closeNav(event)}
            toggleNavHandler={event => this.toggleNav(event)}
          />
          <MiniCart
            minicartOpen={minicartOpen}
            closeMinicart={event => this.closeMinicart(event)}
            toggleMinicartHandler={event => this.toggleMinicart(event)}
          />
          <main className={isHome ? 'home' : location.pathname.replace(/\//g, '')}>
            {children}
          </main>
          <Footer
            location={location}
            settings={settings}
            viewportTopPos={viewportTopPos}
          />
        </div>
      </StoreContext.Provider>
    );
  }
}

class ShopLayout extends Component {
  render() {
    return (
      <StaticQuery
        query={graphql`
          query ShopLayoutQuery {
            settings: prismicSettings {
              data {
                email {
                  text
                }
                phone {
                  text
                }
                primaryMenu: primary_menu {
                  title {
                    text
                  }
                  pageLink: page_link {
                    linkType: link_type
                    uid
                    slug
                    url
                  }
                }
                supportingPages: supporting_pages {
                  title {
                    text
                  }
                  pageLink: page_link {
                    linkType: link_type
                    uid
                    slug
                    url
                  }
                }
                socialLinks: social_links {
                  link {
                    url
                    target
                    linkType: link_type
                  }
                  type
                }
              }
            }
          }
        `}
        render={data => <PureLayout {...this.props} settings={data.settings.data} />}
      />
    );
  }
}

export default ShopLayout;
